import classNames from "classnames";
import DOMPurify from "dompurify";
import moment from "moment/moment";
import React, { FC, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "../../../components/button/Button";
import { StyledDatePicker } from "../../../components/datepicker/datepicker";
import RichText from "../../../components/richtext/RichText";
import CustomSelect from "../../../components/select/Select";
import {
  getInitials,
  getInitialsFromFullName,
} from "../../../constants/constants";
import { WorkDetailsModel } from "../../../factories/work/model/work-details.model";
import {
  toUpdateWorkTaskModel,
  WorkTaskModel,
} from "../../../factories/work/model/work-task.model";
import { useAppSelector } from "../../../redux/redux";
import {
  useAddWorkTaskMutation,
  useUpdateWorkTaskAssigneesMutation,
  useUpdateWorkTaskMutation,
} from "../../../slices/WorkTasksSlice";
import { IUser, SelectOptionType } from "../../../types/types";
import { serverMessage } from "../../../utils/serverMessage";
import { subtaskDueProblem } from "../subtaskDue.utils";

type Props = {
  isActive: boolean;
  onClose: () => void;
  defaultValues?: WorkTaskModel;
  work?: WorkDetailsModel;
  users?: IUser[];
};

type FormData = {
  name: string;
  description: string;
  dueDate: string;
  assignedTo: SelectOptionType[];
};

const MAX_ASSIGNEES = 5;

type UserNameProfile = {
  firstName?: string | null;
  lastName?: string | null;
};

const getValidIsoDate = (date?: string | Date | null): string => {
  return moment(date).isValid()
    ? moment(date).toISOString()
    : moment().toISOString();
};

const getValidDate = (date?: string | Date | null): Date => {
  return moment(date).isValid() ? moment(date).toDate() : new Date();
};

const getUserName = (userProfile?: UserNameProfile | null): string => {
  return `${userProfile?.firstName || ""} ${
    userProfile?.lastName || ""
  }`.trim();
};

const getUserOption = (user?: IUser | null): SelectOptionType | null => {
  if (!user?.id) {
    return null;
  }

  const label = getUserName(user.userProfile);

  return {
    label,
    value: user.id,
    avatar: getInitials(
      user.userProfile?.firstName,
      user.userProfile?.lastName,
    ),
    type: "custom",
    avatarCustomColor: user.userProfile?.color,
  };
};

const getTaskAssigneeOptions = (
  workTask?: WorkTaskModel,
  fallbackUser?: IUser | null,
): SelectOptionType[] => {
  if (!workTask) {
    const fallbackOption = getUserOption(fallbackUser);

    return fallbackOption ? [fallbackOption] : [];
  }

  const assigneeOptions =
    workTask.jobTaskAssignees
      ?.map((item): SelectOptionType | null => {
        const assignedUser = item.assignedUser;

        if (!assignedUser?.id) {
          return null;
        }

        return {
          label: getUserName(assignedUser.userProfile),
          value: assignedUser.id,
          avatar: getInitials(
            assignedUser.userProfile?.firstName,
            assignedUser.userProfile?.lastName,
          ),
          type: "custom" as const,
          avatarCustomColor: assignedUser.userProfile?.color,
        };
      })
      .filter((item): item is SelectOptionType => item !== null) || [];

  if (assigneeOptions.length) {
    return assigneeOptions;
  }

  return workTask.assignedUser
    ? [
        {
          label: getUserName(workTask.assignedUser.userProfile),
          value: workTask.assignedUser.id,
          avatar: getInitials(
            workTask.assignedUser.userProfile?.firstName,
            workTask.assignedUser.userProfile?.lastName,
          ),
          type: "custom",
          avatarCustomColor: workTask.assignedUser.userProfile?.color,
        },
      ]
    : [];
};

const getFormDefaults = (
  defaultValues?: WorkTaskModel,
  user?: IUser | null,
): FormData => {
  return {
    dueDate: getValidIsoDate(defaultValues?.dueDate),
    name: defaultValues?.name || "",
    description: defaultValues?.description || "",
    assignedTo: getTaskAssigneeOptions(defaultValues, user),
  };
};

export const WorkTaskModal: FC<Props> = ({
  isActive,
  onClose,
  defaultValues,
  work,
  users,
}) => {
  const { workId } = useParams();
  const user = useAppSelector((state) => state.appReducer.user);

  const [addWorkTask, { isLoading: isLoadingAdd }] = useAddWorkTaskMutation();
  const [updateWorkTask, { isLoading: isLoadingUpdate }] =
    useUpdateWorkTaskMutation();
  const [updateWorkTaskAssignees, { isLoading: isUpdatingAssignees }] =
    useUpdateWorkTaskAssigneesMutation();

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: getFormDefaults(defaultValues, user),
  });
  const assignedToValue = watch("assignedTo");
  const isEditMode = !!defaultValues;
  const isSaving = isLoadingAdd || isLoadingUpdate || isUpdatingAssignees;
  const maxDueDate = moment(work?.dueDate).isValid()
    ? moment(work?.dueDate).toDate()
    : undefined;

  useEffect(() => {
    if (isActive) {
      reset(getFormDefaults(defaultValues, user));
    }
  }, [defaultValues, isActive, reset, user]);

  const updateExistingTask = async (
    workTask: WorkTaskModel,
    data: FormData,
    safeDescription: string,
  ): Promise<void> => {
    await Promise.all([
      updateWorkTask(
        toUpdateWorkTaskModel(workTask, {
          name: data.name.trim(),
          description: safeDescription,
        }),
      ).unwrap(),
      updateWorkTaskAssignees({
        jobTaskId: workTask.id,
        userIds: data.assignedTo.map((item) => item.value),
      }).unwrap(),
    ]);
  };

  const addNewTask = async (
    data: FormData,
    safeDescription: string,
  ): Promise<void> => {
    await addWorkTask({
      name: data.name.trim(),
      description: safeDescription,
      jobIdBase64: workId || "",
      listOfAssignees: data.assignedTo.map((item) => item.value),
      assignedByUserId: user.id || "",
      dueDate: data.dueDate,
    }).unwrap();
  };

  const onSubmit = async (data: FormData): Promise<void> => {
    if (
      !data.name.trim() ||
      !user?.id ||
      isSaving ||
      (!defaultValues && !workId) ||
      !data.assignedTo.length ||
      data.assignedTo.length > MAX_ASSIGNEES
    ) {
      return;
    }

    const safeDescription = DOMPurify.sanitize(data.description || "");

    // Owner 2026-10-22: a subtask can't be due after its work item.
    const dueProblem = !defaultValues
      ? subtaskDueProblem(data.dueDate, work?.dueDate)
      : null;
    if (dueProblem) {
      toast.error(dueProblem);
      return;
    }

    try {
      if (defaultValues) {
        await updateExistingTask(defaultValues, data, safeDescription);
      } else {
        await addNewTask(data, safeDescription);
      }

      onClose();
    } catch (error) {
      toast.error(
        serverMessage(
          error,
          defaultValues ? "Failed to update subtask" : "Something went wrong",
        ),
      );
    }
  };

  const usersOptions = useMemo<SelectOptionType[]>(() => {
    return (
      users?.map((item) => {
        const firstName = item?.userProfile?.firstName || "";
        const lastName = item?.userProfile?.lastName || "";
        const fullName = `${firstName} ${lastName}`.trim();

        return {
          avatar: getInitialsFromFullName(fullName),
          avatarCustomColor: item?.userProfile?.color,
          label: fullName,
          value: `${item?.id}`,
        };
      }) || []
    );
  }, [users]);

  return (
    <div
      className={classNames("modal", isActive ? "modal-open" : "modal-close")}>
      <div className={"modal-box p-0"}>
        <div
          className={
            "border-b border-gray-300 px-5 py-4 text-center text-lg font-bold"
          }>
          {defaultValues ? "Update" : "Add"} subtask
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={"px-5 py-4"}>
          <div className={"mb-4"}>
            <input
              type={"text"}
              {...register("name", {
                required: "This field is required",
                maxLength: {
                  value: 100,
                  message: "Task name shouldn't be more than 100 characters",
                },
              })}
              className={"input input-bordered w-full"}
              placeholder={"Add subtask title..."}
            />
            {errors?.name?.message && (
              <div className={"mt-2 text-sm font-bold text-error"}>
                {errors?.name?.message}
              </div>
            )}
          </div>
          <div>
            <Controller
              name={"description"}
              control={control}
              defaultValue={defaultValues?.description || ""}
              render={({ field }) => (
                <RichText
                  value={field.value || ""}
                  onChange={field.onChange}
                  outerHeight={200}
                  placeholder={"Add a description (optional)"}
                />
              )}
            />
          </div>
          <div className={"py-2"}>
            <span className={"label-text"}>Assigned to*</span>
            <div className={"my-2"}>
              <Controller
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CustomSelect
                    placeholder={"Select assignee(s)"}
                    options={usersOptions}
                    value={value}
                    onChange={(newValue) => {
                      const nextAssignees = newValue as SelectOptionType[];

                      if (nextAssignees.length <= MAX_ASSIGNEES) {
                        onChange(nextAssignees);
                      }
                    }}
                    withMenu
                    isMulti
                    isError={!!errors?.assignedTo}
                    withAvatar
                    selectStyle={{
                      fitChips: true,
                      fullWidth: true,
                    }}
                  />
                )}
                name={"assignedTo"}
                rules={{
                  validate: (value) =>
                    value.length > 0 || "Select at least one assignee",
                }}
              />
              {errors?.assignedTo?.message && (
                <div className={"mt-2 text-sm font-bold text-error"}>
                  {errors.assignedTo.message}
                </div>
              )}
            </div>
          </div>
          {!isEditMode && (
            <div className={"flex items-center gap-4"}>
              <span className={"label-text"}>Due date:</span>
              <div className={"w-[25%]"}>
                <Controller
                  name={"dueDate"}
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    const handleDateChange = (date: Date | null) => {
                      if (date instanceof Date && !isNaN(date.getTime())) {
                        const isoDate = moment(date).toISOString();
                        onChange(isoDate);
                      }
                    };
                    return (
                      <div onClick={(e) => e.stopPropagation()}>
                        <StyledDatePicker
                          value={getValidDate(value)}
                          onChange={handleDateChange}
                          maxDate={maxDueDate}
                          fontSize={"14px"}
                          format={"MMM dd, yyyy"}
                          placeholder={"MMM DD, YYYY"}
                          acceptTypedDates
                        />
                      </div>
                    );
                  }}
                />
              </div>
            </div>
          )}
          <div className={"mt-4 flex justify-end gap-4"}>
            <Button
              label={isEditMode ? "Update" : "Add"}
              buttonType={"submit"}
              disabled={
                isSaving ||
                (!isEditMode && !workId) ||
                !assignedToValue?.length ||
                assignedToValue.length > MAX_ASSIGNEES
              }
              isLoading={isSaving}
            />
            <Button label={"Cancel"} onClick={onClose} colorType={"outline"} />
          </div>
        </form>
      </div>
    </div>
  );
};
